import { InputNumber } from "antd";
import { type InputNumberProps } from "antd/lib/input-number";
import { memo, useContext, useEffect, useRef, useState } from "react";

import { cn } from "@btnet/utilities-core";

import { CurrencyLocale } from "helpers/AppProvider.types";
import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

import { track, useTracking } from "utilities/analytics/analytics";
import { round } from "utilities/math/math";
import { getAccuracy } from "utilities/number/number";
import { isNullOrUndefined } from "utilities/object/object";
import { type KeyOfOrString } from "utilities/type/PropsOfType";

import { NumberDisplay } from "commonComponents/utilities/NumberDisplay/NumberDisplay";
import { type ReadOnlyShouldTruncate } from "commonComponents/utilities/TextTruncate/TextTruncate.type";
import {
    isReadOnly,
    shouldTruncate,
} from "commonComponents/utilities/TextTruncate/TextTruncate.utility";
import { ValueDisplay } from "commonComponents/utilities/ValueDisplay/ValueDisplay";

import "./BTInputNumber.less";

/** Use this to override the internal styles set in BTInputNumber with Antd's styles */
export const defaultAntStyle = {};

export interface IBTInputNumberProps<FormValues>
    extends Omit<
        InputNumberProps,
        "onChange" | "name" | "value" | "id" | "min" | "max" | "placeholder" | "onBlur" | "readOnly"
    > {
    id: KeyOfOrString<FormValues> & string;

    min: number;

    max: number;

    "data-testid": string;
    readOnly?: ReadOnlyShouldTruncate;

    value: number | null | undefined;

    /** pass formik setFieldValue */
    onChange: (field: string, value: number | null | undefined) => void;

    /** pass formik setFieldTouched */
    onBlur?: (field: string, touched: boolean) => void;

    roundToDecimalPlaces?: number;
    allowNull?: boolean;
    removeTrailingZerosOnEdit?: boolean;

    /**
     * The label text displayed before (on the left side of) the input field
     */
    addonBefore?: React.ReactNode;
    /**
     * The label text displayed after (on the right side of) the input field
     */
    addonAfter?: React.ReactNode;

    /**
     * Controls the form interaction mode. When set to "click-to-edit", the input appears
     * like a read-only field with transparent borders, becoming editable on hover/focus.
     * @default "always-editing"
     */
    formMode?: "always-editing" | "click-to-edit";
}

declare type ValueType = string | number;

const BTInputNumberInternal = track((props) => ({
    element: "Number Input",
    uniqueId: props["data-testid"],
}))(function <FormValues = undefined>({
    id,
    min,
    max,
    "data-testid": testid,
    readOnly,
    value,
    onChange,
    onBlur,
    roundToDecimalPlaces,
    allowNull = false,
    removeTrailingZerosOnEdit = false,
    addonBefore,
    addonAfter,
    step = 1,
    formMode = "always-editing",
    ...otherProps
}: IBTInputNumberProps<FormValues>) {
    const currencyLocale =
        useContext(BuilderInfoContext)?.locale.currencyLocale ?? CurrencyLocale.default;
    const isDirtyInternalRef = useRef(false);
    const { trackEvent } = useTracking();
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        setIsFocused(false);
    }, [formMode]);

    const isClickToEdit = formMode === "click-to-edit";
    const isClickToEditViewMode = isClickToEdit && !isFocused;

    let inputValue: ValueType | undefined = value ?? undefined;
    if (!isDirtyInternalRef.current && removeTrailingZerosOnEdit && !isNullOrUndefined(value)) {
        const decimalCount = Math.min(getAccuracy(value), otherProps.precision ?? 2);
        // We need to display only up to {precision} number of decimals in edit mode, even before the user makes a change
        inputValue = value.toFixed(decimalCount);
    }

    function handleChangeInternal(value: number | string | null | undefined) {
        trackEvent({
            event: "ValueChange",
            uniqueId: testid,
        });
        isDirtyInternalRef.current = true;
        if (allowNull && value === null) {
            onChange(id, null);
            return;
        }

        const valueNotNull = value === null ? undefined : value;

        let inputAsNumber = Number(valueNotNull);
        if (isNaN(inputAsNumber)) {
            // Invalid user input. Try to default to 0 if possible
            onChange(String(id), Math.min(Math.max(0, min), max));
            return;
        }

        if (roundToDecimalPlaces !== undefined) {
            inputAsNumber = round(inputAsNumber, roundToDecimalPlaces);
        }

        // We do not want validation errors for when number input is out of range. Ant Design constrains the value between
        // the min and max range, but only on blur, meaning a validation error will occur/display until then
        const constrainedInput = Math.min(Math.max(inputAsNumber, min), max);

        onChange(String(id), constrainedInput);
    }

    function handleBlur() {
        setIsFocused(false);
        if (onBlur) {
            onBlur(id, true);
        }
    }

    function handleFocus() {
        setIsFocused(true);
    }

    function removeTrailingZeros(value: ValueType | undefined) {
        if (value && typeof value === "string") {
            const noZeros = parseFloat(value);
            if (isNaN(noZeros)) {
                return "";
            }
            return noZeros.toString();
        }
        return "";
    }

    if (isReadOnly(readOnly)) {
        let displayValue;
        if (!isNullOrUndefined(value)) {
            displayValue = (
                <NumberDisplay
                    value={value}
                    decimalScale={otherProps.precision ?? Math.max(0, getAccuracy(value))}
                    shouldTruncate={shouldTruncate(readOnly)}
                    suffix={typeof addonAfter === "string" ? addonAfter : undefined}
                />
            );
        }

        return (
            <ValueDisplay
                id={id}
                data-testid={testid}
                tabIndex={otherProps.tabIndex}
                value={displayValue}
                className={cn({
                    "padding-horizontal-xs": formMode === "click-to-edit",
                })}
            />
        );
    }

    const pattern = `[0-9${currencyLocale.decimalSeparator}-]*`;

    const component = (
        <InputNumber
            name={id}
            data-testid={testid}
            value={inputValue}
            style={{ width: "100%" }}
            decimalSeparator={currencyLocale.decimalSeparator}
            formatter={
                removeTrailingZerosOnEdit ? (value) => removeTrailingZeros(value) : undefined
            }
            onChange={handleChangeInternal}
            inputMode={otherProps.precision === 0 ? "numeric" : "decimal"}
            pattern={pattern}
            onBlur={handleBlur}
            onFocus={handleFocus}
            step={step}
            {...otherProps}
        />
    );

    const wrapperClassName = cn("BTInputNumber", {
        "BTInputNumber--ClickToEditViewMode": isClickToEditViewMode,
    });

    if (addonBefore || addonAfter) {
        return (
            <span className={wrapperClassName}>
                <span className="ant-input-group-wrapper">
                    <span className="ant-input-wrapper ant-input-group">
                        {addonBefore && (
                            <span className="ant-input-group-addon">{addonBefore}</span>
                        )}
                        {component}
                        {addonAfter && <span className="ant-input-group-addon">{addonAfter}</span>}
                    </span>
                </span>
            </span>
        );
    }

    return <span className={wrapperClassName}>{component}</span>;
});

export const BTInputNumber = memo(BTInputNumberInternal) as typeof BTInputNumberInternal;
